import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import apiClient from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { useHelp } from './HelpContext';
import HELP_TOPICS, { HelpSection, HelpTopic } from './helpContent';
import { guideForPath, HelpGuide, searchGuides } from './helpGuides';

type View = 'home' | 'guide' | 'support' | 'reference';

function highlightControl(targetText?: string): boolean {
  if (!targetText) return false;
  const candidates = Array.from(document.querySelectorAll<HTMLElement>(
    'button, a, [role="button"], [aria-label], [title]'
  ));
  const needle = targetText.toLowerCase();
  const target = candidates.find((element) => {
    if (element.closest('[data-help-drawer="true"]')) return false;
    const label = [element.innerText, element.getAttribute('aria-label'), element.getAttribute('title')]
      .filter(Boolean).join(' ').toLowerCase();
    return label.includes(needle);
  });
  if (!target) return false;

  const previousOutline = target.style.outline;
  const previousOffset = target.style.outlineOffset;
  target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  target.style.outline = '3px solid #f59e0b';
  target.style.outlineOffset = '4px';
  window.setTimeout(() => {
    target.style.outline = previousOutline;
    target.style.outlineOffset = previousOffset;
  }, 4000);
  return true;
}

const SectionView: React.FC<{ section: HelpSection }> = ({ section }) => (
  <section style={styles.referenceSection}>
    <h4 style={styles.sectionHeading}>{section.heading}</h4>
    <p style={styles.paragraph}>{section.body}</p>
    {section.formula && <pre style={styles.formula}>{section.formula}</pre>}
    {section.note && <div style={styles.note}>{section.note}</div>}
    {section.table && (
      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead><tr>{section.table.headers.map((header) => <th key={header} style={styles.cell}>{header}</th>)}</tr></thead>
          <tbody>{section.table.rows.map((row, index) => (
            <tr key={index}>{row.map((cell, cellIndex) => <td key={cellIndex} style={styles.cell}>{cell}</td>)}</tr>
          ))}</tbody>
        </table>
      </div>
    )}
  </section>
);

const ReferenceView: React.FC<{ topic: HelpTopic; onBack: () => void }> = ({ topic, onBack }) => (
  <div>
    <button type="button" onClick={onBack} style={styles.textButton}>← Back</button>
    <h3 style={styles.title}>{topic.icon} {topic.title}</h3>
    <p style={styles.muted}>{topic.brief}</p>
    {topic.sections.map((section, index) => <SectionView key={index} section={section} />)}
  </div>
);

const GuideView: React.FC<{
  guide: HelpGuide;
  onBack: () => void;
}> = ({ guide, onBack }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [highlightMessage, setHighlightMessage] = useState('');
  const step = guide.steps[stepIndex];

  useEffect(() => {
    setStepIndex(0);
    setHighlightMessage('');
  }, [guide.id]);

  const showTarget = () => {
    setHighlightMessage(highlightControl(step.targetText)
      ? 'Highlighted on the page for four seconds.'
      : 'That control is not visible on this page yet. Follow the step, then try again.');
  };

  return (
    <div>
      <button type="button" onClick={onBack} style={styles.textButton}>← All help</button>
      <div style={styles.eyebrow}>Guided walkthrough</div>
      <h3 style={styles.title}>{guide.title}</h3>
      <p style={styles.muted}>{guide.summary}</p>

      <div style={styles.progressTrack} aria-label={`Step ${stepIndex + 1} of ${guide.steps.length}`}>
        <div style={{ ...styles.progressBar, width: `${((stepIndex + 1) / guide.steps.length) * 100}%` }} />
      </div>
      <div style={styles.stepCount}>Step {stepIndex + 1} of {guide.steps.length}</div>
      <div style={styles.stepCard}>
        <h4 style={{ ...styles.sectionHeading, marginTop: 0 }}>{step.title}</h4>
        <p style={styles.paragraph}>{step.body}</p>
        {step.targetText && (
          <button type="button" onClick={showTarget} style={styles.secondaryButton}>Show me on this page</button>
        )}
        {highlightMessage && <div role="status" style={styles.smallMessage}>{highlightMessage}</div>}
      </div>

      <div style={styles.buttonRow}>
        <button type="button" disabled={stepIndex === 0} onClick={() => { setStepIndex(stepIndex - 1); setHighlightMessage(''); }} style={styles.secondaryButton}>Back</button>
        <button type="button" onClick={() => {
          if (stepIndex === guide.steps.length - 1) onBack();
          else { setStepIndex(stepIndex + 1); setHighlightMessage(''); }
        }} style={styles.primaryButton}>
          {stepIndex === guide.steps.length - 1 ? 'Finish' : 'Next'}
        </button>
      </div>
    </div>
  );
};

const SupportView: React.FC<{ pathname: string; onBack: () => void }> = ({ pathname, onBack }) => {
  const [kind, setKind] = useState<'support' | 'bug'>('support');
  const [description, setDescription] = useState('');
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [reportId, setReportId] = useState('');

  const submit = async () => {
    if (!description.trim() || !consent) return;
    setStatus('sending');
    try {
      const response = await apiClient.post('/admin/bug-report', {
        kind,
        description: description.trim(),
        page_path: pathname,
        app_version: process.env.REACT_APP_VERSION || 'unknown',
        timestamp: new Date().toISOString(),
      });
      setReportId(response.data?.report_id || 'submitted');
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div>
      <button type="button" onClick={onBack} style={styles.textButton}>← All help</button>
      <div style={styles.eyebrow}>Support</div>
      <h3 style={styles.title}>Ask for help or report a problem</h3>
      <p style={styles.muted}>Tell the Adroit team what you were trying to do. This form sends only what you type, this page path, your account and tenant identifiers, and the app version.</p>

      <label style={styles.label}>Request type
        <select value={kind} onChange={(event) => setKind(event.target.value as 'support' | 'bug')} style={styles.input}>
          <option value="support">Question or request</option>
          <option value="bug">Report a problem</option>
        </select>
      </label>
      <label style={styles.label}>What do you need help with?
        <textarea value={description} maxLength={5000} onChange={(event) => setDescription(event.target.value)} placeholder="Describe what you were doing, what happened and what you expected." style={{ ...styles.input, minHeight: 130, resize: 'vertical' }} />
      </label>

      <div style={styles.privacyBox}>
        <strong>No automatic scientific-data capture.</strong> Rows, molecular structures, files, screenshots, query strings and browser history are not attached. Do not paste confidential scientific data into this box.
      </div>
      <label style={styles.consentLabel}>
        <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />
        I have checked my message and it is safe to send to the Adroit support team.
      </label>

      {status === 'sent' ? (
        <div role="status" style={styles.success}>Sent securely. Reference: {reportId}</div>
      ) : (
        <button type="button" disabled={!description.trim() || !consent || status === 'sending'} onClick={submit} style={{ ...styles.primaryButton, width: '100%' }}>
          {status === 'sending' ? 'Sending…' : 'Send to support'}
        </button>
      )}
      {status === 'error' && <div role="alert" style={styles.error}>We could not send this request. Please try again or contact your administrator.</div>}
    </div>
  );
};

const HelpPanel: React.FC = () => {
  const { state, close } = useHelp();
  const { user } = useAuth();
  const location = useLocation();
  const contextualGuide = useMemo(() => guideForPath(location.pathname), [location.pathname]);
  const [view, setView] = useState<View>('home');
  const [selectedGuide, setSelectedGuide] = useState<HelpGuide>(contextualGuide);
  const [selectedTopic, setSelectedTopic] = useState<HelpTopic | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    setSelectedGuide(contextualGuide);
    setView('home');
    setQuery('');
  }, [contextualGuide]);

  useEffect(() => {
    if (!state.topic) return;
    const topic = HELP_TOPICS[state.topic];
    if (topic) {
      setSelectedTopic(topic);
      setView('reference');
    }
  }, [state.topic]);

  if (!state.isOpen) return null;
  const results = searchGuides(query, location.pathname);
  const goHome = () => { setView('home'); setSelectedTopic(null); };
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const openDiagnostics = () => {
    close();
    window.dispatchEvent(new CustomEvent('adroit:open-debug-panel'));
  };

  return (
    <aside data-help-drawer="true" aria-label="Help and guided walkthroughs" style={styles.panel}>
      <header style={styles.header}>
        <div>
          <div style={styles.eyebrow}>Adroit help</div>
          <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>How can we help?</div>
        </div>
        <button type="button" onClick={close} aria-label="Close help" style={styles.closeButton}>×</button>
      </header>

      <div style={styles.content}>
        {view === 'home' && (
          <>
            <label style={styles.label}>What would you like to do?
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="For example: make a scatter plot" style={styles.input} autoFocus />
            </label>

            {!query && (
              <button type="button" onClick={() => { setSelectedGuide(contextualGuide); setView('guide'); }} style={styles.contextCard}>
                <span style={styles.contextBadge}>Recommended for this page</span>
                <strong style={{ display: 'block', marginTop: 8 }}>{contextualGuide.title}</strong>
                <span style={styles.muted}>{contextualGuide.summary}</span>
              </button>
            )}

            <div style={styles.sectionLabel}>{query ? 'Suggested help' : 'Walkthroughs'}</div>
            {results.map((guide) => (
              <button key={guide.id} type="button" onClick={() => { setSelectedGuide(guide); setView('guide'); }} style={styles.listButton}>
                <strong>{guide.title}</strong>
                <span style={styles.muted}>{guide.summary}</span>
              </button>
            ))}
            {query && results.length === 0 && <div style={styles.empty}>No walkthrough matched that question. Send it to support below.</div>}

            <button type="button" onClick={() => setView('support')} style={{ ...styles.secondaryButton, width: '100%', marginTop: 12 }}>Ask support or report a problem</button>

            {isAdmin && (
              <>
                <div style={styles.sectionLabel}>Administrator tools</div>
                <button type="button" onClick={openDiagnostics} style={styles.compactButton}>
                  <span aria-hidden="true">⚙</span><span>Open diagnostic panel</span>
                </button>
              </>
            )}

            <div style={styles.sectionLabel}>Scientific reference</div>
            {Object.values(HELP_TOPICS).map((topic) => (
              <button key={topic.id} type="button" onClick={() => { setSelectedTopic(topic); setView('reference'); }} style={styles.compactButton}>
                <span>{topic.icon}</span><span>{topic.title}</span>
              </button>
            ))}
          </>
        )}
        {view === 'guide' && <GuideView guide={selectedGuide} onBack={goHome} />}
        {view === 'support' && <SupportView pathname={location.pathname} onBack={goHome} />}
        {view === 'reference' && selectedTopic && <ReferenceView topic={selectedTopic} onBack={goHome} />}
      </div>
      <footer style={styles.footer}>Guidance is built into this application. No page data is sent to an external AI service.</footer>
    </aside>
  );
};

const styles: Record<string, React.CSSProperties> = {
  panel: { position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(440px, 100vw)', zIndex: 9000, background: 'var(--card-bg)', borderLeft: '1px solid var(--card-border)', boxShadow: '-8px 0 28px rgba(15, 23, 42, 0.16)', display: 'flex', flexDirection: 'column', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
  header: { minHeight: 68, padding: '12px 16px', borderBottom: '1px solid var(--card-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  closeButton: { border: 0, background: 'transparent', color: 'var(--text-muted)', fontSize: 28, lineHeight: 1, cursor: 'pointer', padding: '4px 8px' },
  content: { flex: 1, overflowY: 'auto', padding: 18 },
  footer: { padding: '10px 16px', borderTop: '1px solid var(--card-border)', color: 'var(--text-muted)', fontSize: 11, lineHeight: 1.45 },
  eyebrow: { color: '#0d9488', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' },
  title: { color: 'var(--text-primary)', fontSize: 20, lineHeight: 1.25, margin: '8px 0 6px' },
  muted: { display: 'block', color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.5, fontWeight: 400, marginTop: 4 },
  paragraph: { color: 'var(--text-primary)', fontSize: 13, lineHeight: 1.6, margin: '6px 0 12px', whiteSpace: 'pre-wrap' },
  label: { display: 'block', color: 'var(--text-primary)', fontSize: 12, fontWeight: 650, marginBottom: 14 },
  input: { display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '10px 11px', border: '1px solid var(--card-border)', borderRadius: 8, background: 'var(--input-bg, var(--card-bg))', color: 'var(--text-primary)', font: 'inherit', fontWeight: 400 },
  contextCard: { width: '100%', textAlign: 'left', padding: 14, border: '1px solid #5eead4', borderRadius: 10, background: 'rgba(20, 184, 166, 0.08)', color: 'var(--text-primary)', cursor: 'pointer' },
  contextBadge: { color: '#0f766e', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' },
  sectionLabel: { margin: '20px 0 8px', color: 'var(--text-muted)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' },
  listButton: { width: '100%', display: 'block', textAlign: 'left', padding: '10px 12px', marginBottom: 6, border: '1px solid var(--card-border)', borderRadius: 8, background: 'var(--card-bg)', color: 'var(--text-primary)', cursor: 'pointer' },
  compactButton: { width: '100%', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', padding: '8px 10px', marginBottom: 5, border: '1px solid var(--card-border)', borderRadius: 7, background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 12 },
  textButton: { border: 0, background: 'transparent', color: '#0d9488', cursor: 'pointer', padding: '0 0 10px', fontSize: 12, fontWeight: 650 },
  primaryButton: { padding: '9px 15px', border: 0, borderRadius: 7, background: '#0d9488', color: '#fff', fontWeight: 700, cursor: 'pointer' },
  secondaryButton: { padding: '8px 12px', border: '1px solid var(--card-border)', borderRadius: 7, background: 'var(--card-bg)', color: 'var(--text-primary)', fontWeight: 650, cursor: 'pointer' },
  buttonRow: { display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 16 },
  progressTrack: { height: 5, borderRadius: 4, background: 'var(--card-border)', overflow: 'hidden', marginTop: 18 },
  progressBar: { height: '100%', background: '#14b8a6', transition: 'width 180ms ease' },
  stepCount: { color: 'var(--text-muted)', fontSize: 11, margin: '6px 0 10px' },
  stepCard: { border: '1px solid var(--card-border)', borderRadius: 10, padding: 14, background: 'rgba(148, 163, 184, 0.06)' },
  smallMessage: { color: 'var(--text-muted)', fontSize: 11, marginTop: 9 },
  sectionHeading: { color: 'var(--text-primary)', fontSize: 14, margin: '16px 0 6px' },
  referenceSection: { borderBottom: '1px solid var(--card-border)', paddingBottom: 8 },
  formula: { whiteSpace: 'pre-wrap', padding: 10, borderRadius: 7, background: 'rgba(20, 184, 166, 0.08)', color: 'var(--text-primary)', fontSize: 12 },
  note: { padding: 10, borderLeft: '3px solid #f59e0b', background: 'rgba(245, 158, 11, 0.08)', color: 'var(--text-primary)', fontSize: 12, lineHeight: 1.5 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 11 },
  cell: { padding: 6, textAlign: 'left', borderBottom: '1px solid var(--card-border)', color: 'var(--text-primary)' },
  privacyBox: { padding: 11, borderRadius: 8, border: '1px solid #fcd34d', background: 'rgba(245, 158, 11, 0.08)', color: 'var(--text-primary)', fontSize: 11, lineHeight: 1.5, margin: '10px 0' },
  consentLabel: { display: 'flex', gap: 8, alignItems: 'flex-start', color: 'var(--text-muted)', fontSize: 11, lineHeight: 1.45, margin: '12px 0' },
  success: { padding: 11, borderRadius: 8, background: 'rgba(16, 185, 129, 0.12)', color: '#047857', fontSize: 12 },
  error: { marginTop: 10, color: '#dc2626', fontSize: 12 },
  empty: { padding: 12, color: 'var(--text-muted)', fontSize: 12, textAlign: 'center' },
};

export default HelpPanel;
